import React, { Component } from 'react'
import trans from "trans"

import { Button, Icon, TextInput, Theme, css } from "@bdfx/mergado-ui-kit"

import { CustomImageSetFile } from "app/types/model-types"
import { updateCustomImageSetFileSourceInfo } from "app/helpers/api"
import { AxiosResponse } from "axios"
import store from "store"
import { createToast } from "actions"
import toastFactory from "app/helpers/toast_factory"
import { GENERAL_ERROR, SAVED_OK } from "template-edit/reducers"

type SourceInfo = Pick<CustomImageSetFile, 'source_url' | 'license' | 'author'>

interface Props {
    setFile: CustomImageSetFile
    onUpdated?: (setFile: CustomImageSetFile) => void
}

interface State {
    isEditing: boolean
    isSaving: boolean
    values: SourceInfo
}

/**
 * Author, license and source of an uploaded custom image, with inline editing.
 */
export const getSourceInfoTitle = (setFile: CustomImageSetFile): string => {
    return [
        setFile.orig_filename || setFile.filename,
        setFile.author ? `${trans('custom_image_author')}: ${setFile.author}` : null,
        setFile.license ? `${trans('custom_image_license')}: ${setFile.license}` : null,
        setFile.source_url ? `${trans('custom_image_source_url')}: ${setFile.source_url}` : null,
    ].filter(Boolean).join('\n')
}

const isHttpUrl = (url: string) => /^https?:\/\//i.test(url)

class CustomSetSourceInfo extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isEditing: false,
            isSaving: false,
            values: this.getValues(),
        }
    }

    getValues = (): SourceInfo => {
        const { setFile } = this.props

        return {
            source_url: setFile.source_url || '',
            license: setFile.license || '',
            author: setFile.author || '',
        }
    }

    startEditing = () => {
        this.setState({ isEditing: true, values: this.getValues() })
    }

    handleChange = (key: keyof SourceInfo, value: string) => {
        this.setState({ values: { ...this.state.values, [key]: value } })
    }

    handleSave = () => {
        const { setFile, onUpdated } = this.props

        this.setState({ isSaving: true })
        updateCustomImageSetFileSourceInfo(setFile, this.state.values)
            .then((response: AxiosResponse<CustomImageSetFile>) => {
                this.setState({ isEditing: false })
                store.dispatch(createToast(toastFactory(SAVED_OK)))
                onUpdated && onUpdated({ ...setFile, ...response.data })
            })
            .catch(error => {
                console.error('cannot update custom image source info', error)
                store.dispatch(createToast(toastFactory(GENERAL_ERROR, error)))
            })
            .finally(() => this.setState({ isSaving: false }))
    }

    render() {
        const { setFile } = this.props
        const { isEditing, isSaving, values } = this.state

        if (isEditing) {
            return (
                <StyledSourceInfo className={isSaving ? 'muk-helper-loading-line' : ''}>
                    {(['author', 'license', 'source_url'] as (keyof SourceInfo)[]).map(key =>
                        <TextInput
                            key={key}
                            small={true}
                            label={trans(`custom_image_${key}`)}
                            value={values[key]}
                            onChange={(value: string) => this.handleChange(key, value)}
                        />
                    )}
                    <div className="buttons">
                        <Button size={'small'} type={'void'} disabled={isSaving} onClick={this.handleSave}
                            icon={<Icon type={'check'} text={trans('save')} />} />
                        <Button size={'small'} type={'void'} secondary={true} disabled={isSaving} onClick={() => this.setState({ isEditing: false })}
                            icon={<Icon type={'close-light'} text={trans('cancel')} />} />
                    </div>
                </StyledSourceInfo>
            )
        }

        const hasInfo = setFile.author || setFile.license || setFile.source_url

        return (
            <StyledSourceInfo>
                <span className="edit" title={trans('custom_image_edit_source_info')} onClick={this.startEditing}>
                    <Icon type={'edit-light'} size={14} color={Theme.blue} />
                </span>
                {!hasInfo && <span className="empty">{trans('custom_image_no_source_info')}</span>}
                {setFile.author && <div title={trans('custom_image_author')}>© {setFile.author}</div>}
                {setFile.license && <div title={trans('custom_image_license')}>{setFile.license}</div>}
                {setFile.source_url && (isHttpUrl(setFile.source_url)
                    ? <a href={setFile.source_url} target="_blank" rel="noopener noreferrer" title={setFile.source_url}>{trans('custom_image_source_url')}</a>
                    : <div title={trans('custom_image_source_url')}>{setFile.source_url}</div>
                )}
            </StyledSourceInfo>
        )
    }
}

const StyledSourceInfo = css('div')({
    position: 'relative',
    marginTop: '5px',
    paddingRight: '18px',
    fontSize: '12px',
    textAlign: 'left',
    wordBreak: 'break-word',
    cursor: 'default',
    ' .edit': {
        position: 'absolute',
        top: 0,
        right: 0,
        cursor: 'pointer',
    },
    ' .empty': {
        color: '#888',
    },
    ' .buttons': {
        display: 'flex',
        gap: '5px',
    },
    ' .muk-form-row': {
        marginBottom: '5px',
    },
})

export default CustomSetSourceInfo
